import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { PROGRAMS, MOVE_MODULES, GRIEF_PATHWAYS, ASSESSMENT_URL } from '../data/programs';

export default function Programs() {
  return (
    <div className="overflow-hidden" data-testid="programs-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#FAFAF8] relative" data-testid="programs-hero">
        <div className="absolute inset-0 grain-overlay" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-sm font-bold uppercase tracking-widest text-[#C9A962] mb-4 block">
              The M.O.V.E.™ Course System
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-[#1B365D] mb-6">
              One Framework, Tailored to Your Loss
            </h1>
            <p className="text-lg md:text-xl text-[#1B365D]/70 leading-relaxed">
              Dr. A doesn't offer different programs for different grief types. She offers one
              structured M.O.V.E.™ framework — tailored to the specific loss you're navigating — so
              the process fits your experience without ever losing its structure.
            </p>
          </div>
        </div>
      </section>

      {/* The Framework - Four Modules */}
      <section className="py-24 md:py-32 bg-white" data-testid="programs-modules">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sm font-bold uppercase tracking-widest text-[#C9A962] mb-4 block">
              The Engine
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1B365D] mb-4">
              The Four M.O.V.E.™ Modules
            </h2>
            <p className="text-lg text-[#1B365D]/70">
              A clinically grounded core that never changes — regardless of the type of loss — giving
              every journey a rigorous, evidence-informed structure.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {MOVE_MODULES.map((mod) => (
              <div
                key={mod.number}
                className="bg-[#F5F5F0] rounded-2xl p-8 border border-[#C9A962]/20"
                data-testid={`module-${mod.number}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <span className="font-serif text-3xl font-bold text-[#C9A962]">{mod.number}</span>
                  <h3 className="font-serif text-xl font-bold text-[#1B365D]">{mod.title}</h3>
                </div>
                <p className="text-[#1B365D]/70 leading-relaxed">{mod.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Grief Pathways */}
      <section className="py-24 bg-[#1B365D] relative" data-testid="programs-pathways">
        <div className="absolute inset-0 grain-overlay" />
        <div className="container-custom relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sm font-bold uppercase tracking-widest text-[#C9A962] mb-4 block">
              Tailored Pathways
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">
              Grief Is Not Monolithic
            </h2>
            <p className="text-lg text-white/70">
              Choose your pathway and the framework adapts its language, exercises, and examples to
              the specific texture of your grief.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {GRIEF_PATHWAYS.map((path) => (
              <div
                key={path.title}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
              >
                <h3 className="font-serif text-lg font-bold text-[#C9A962] mb-3">{path.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{path.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Programs (Value Ladder) */}
      <section className="py-24 md:py-32 bg-[#FAFAF8]" data-testid="programs-list">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sm font-bold uppercase tracking-widest text-[#C9A962] mb-4 block">
              Ways to Begin
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1B365D] mb-4">
              Choose Your Entry Point
            </h2>
            <p className="text-lg text-[#1B365D]/70">
              From a single introductory workshop to the flagship cohort, there's a structured path
              for wherever you are right now.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {PROGRAMS.map((program, index) => (
              <Link
                to={`/programs/${program.slug}`}
                key={program.slug}
                className="service-card group relative overflow-hidden flex flex-col"
                data-testid={`program-card-${program.slug}`}
                style={{ animationDelay: `${index * 80}ms` }}
              >
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${program.color}`} />
                <div className="pt-4 flex flex-col h-full">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-bold uppercase tracking-wider text-[#C9A962]">
                      {program.badge}
                    </span>
                    <span className="font-serif text-2xl font-bold text-[#1B365D]">
                      {program.price}
                    </span>
                  </div>

                  <h3 className="font-serif text-xl font-bold text-[#1B365D] mb-2 group-hover:text-[#2D7D7B] transition-colors">
                    {program.name}
                  </h3>
                  <p className="text-sm text-[#1B365D]/60 mb-3">{program.duration}</p>
                  <p className="text-[#1B365D]/75 leading-relaxed mb-6 flex-grow">
                    {program.tagline}
                  </p>

                  <span className="text-[#2D7D7B] font-medium flex items-center gap-1 group-hover:gap-2 transition-all mt-auto">
                    View Program <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Not sure where to start */}
      <section className="py-24 bg-[#2D7D7B] relative" data-testid="programs-cta">
        <div className="absolute inset-0 grain-overlay" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6">
              Not Sure Where to Start?
            </h2>
            <p className="text-lg text-white/80 mb-8 leading-relaxed">
              Take the grief assessment to identify your pathway and discover which M.O.V.E.™ program
              best fits where you are in your journey.
            </p>
            <a
              href={ASSESSMENT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-[#2D7D7B] hover:bg-[#F5F5F0] px-8 py-4 rounded-full font-bold transition-colors inline-flex items-center justify-center gap-2"
              data-testid="programs-assessment-cta"
            >
              Take the Grief Assessment
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
